import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import getProjectIdFromConfiguredCollections from "components/ProjectSpecificComponents/Functions/Data/getProjectIdFromConfiguredCollections"
import parseForSubmitRequest from "components/ProjectSpecificComponents/Functions/parseForSubmitRequest"
import {
	ICollection,
	INewRequestFormField,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	IPOST_RESPONSE_Requests,
	TItemOfTypeValues,
	TItemTypeFields,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import { TReactSelectOption } from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { observer } from "mobx-react-lite"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import SepLine from "../../Global/atoms/SepLine"
import FormFieldLabel from "../atoms/FormFieldLabel"
import UserSideNewRequestForm, {
	IFormVal,
	TOnSubmitNewRequestForm,
} from "../molecules/Forms/UserSideNewRequestForm"
import { UserFormWelcomeText } from "../molecules/Forms/UserFormWelcomeText"
import readIdFromName from "../atoms/readIdFromItemName"
import { UserContext } from "components/ProjectSpecificComponents/Contexts/UserContext"
import { lostOrFinishedUserSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedUserSession"
import { replaceMultipleInString } from "components/ProjectNonSpecificComponents/Functions/replaceMultipleInString"
import LoadingText from "components/ProjectNonSpecificComponents/Elements/Loader/LoadingText"

interface IItemOfTypeOptions {
	[key: string]: TItemOfTypeValues[]
}

interface Props {}

export const USER_REQ_CLASSNAME = "user-req-input-field"
/**
 * Generates request from from values stored in userSettings.json
 */
const UserSubmitRequest = observer(({}: Props) => {
	const store = useStore()
	const formStore = useFormStore()
	const { mergeUserFormFields } = formStore
	const { isMobile } = useScreenType()
	const { t, i18n } = useTranslation()
	const { setUser } = useContext(UserContext)
	// ! TECHNICAL DEBT: 3184, replace this with dynamic key ([parsedkey])
	let isEN = i18n.language == "en"
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false)
	const [triggerClearValues, setTriggerClearValues] = useState<boolean>(true)

	const [collection, setCollection] = useState<ICollection[]>([])
	const [picklistFieldId, setPicklistFieldId] = useState<string>()

	const [itemFields, setItemFields] = useState<TItemTypeFields[]>([])
	const [itemOfTypeOptionsForm] = useState<IItemOfTypeOptions>({})
	const [failedFileNames, setFailedFileNames] = useState<string[]>([])

	const { instanceAnonymousUsers } = store
	// Because most bots try to submit instantly after component render
	// For that reason there is 3 second delay after component render before you can submit
	useEffect(() => {
		setTimeout(() => {
			setIsSubmitEnabled(true)
		}, 3000)
	}, [])

	async function sendAttachments(files: File[], projectId: string, itemId: string, key: string) {
		setFailedFileNames([])
		for (const file of files) {
			const formData = new FormData()
			formData.append("ProjectId", projectId)
			formData.append("ItemId", itemId)
			formData.append("Name", file.name)
			formData.append("Content", file)
			const { ok } = await AppRequests("Requests").post<FormData>("attachment", formData)

			if (!ok) {
				failedFileNames.push(file.name)
			}
		}
	}

	const {
		isEnabled = false,
		notificationTextLV = "",
		notificationTextEN = "",
	} = formStore.settingsData.additionalInfo?.submitText || {
		isEnabled: false,
		notificationText: "",
	}

	/**
	 * Fired when request form is submitted
	 */
	const onUserSubmitsNewRequest: TOnSubmitNewRequestForm = async (userRequestFormData, files) => {
		// Cant submit 3 seconds after component rendered because most bots try to submit instantly after component render
		if (!isSubmitEnabled) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "newRequest.failed.header",
				descriptionKey: "",
			})
			store.setLoaderOverlayOpen(false)
			return
		}
		if (!formStore.userSelectedForm) return
		// hd-password and hd-name are hidden fields to catch bots using Honeypot captcha method
		if (!!userRequestFormData["hd-password"] || !!userRequestFormData["hd-name"]) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "newRequest.failed.header",
				descriptionKey: "newRequest.failed.no-bots",
			})
			store.setLoaderOverlayOpen(false)
			setTriggerClearValues(true)
			return
		}
		let requiredFieldsFilled = formStore.userFormFields.every(({ isRequired, mainFieldKey }) => {
			return isRequired ? !!userRequestFormData[mainFieldKey] : true
		})

		document.querySelectorAll(`.${USER_REQ_CLASSNAME}`).forEach((e: any) => {
			switch (e.nodeName) {
				case "DIV":
					return e.classList.toggle("req-input", !e?.textContent)
				case "INPUT":
					return e.classList.toggle("req-input", !e?.value)
				case "TEXTAREA":
					return e.classList.toggle("req-input", !e?.value)
				// BECAUSE ONLY BUTTON IN USER REQUEST FOR NOW IS DATE
				case "BUTTON":
					return e.classList.toggle("req-input", e?.textContent == "dd/mm/yyyy")
			}
		})

		if (!requiredFieldsFilled) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "newRequest.failed.header",
				descriptionKey: "newRequest.failed.required-fields",
			})
			store.setLoaderOverlayOpen(false)
			return
		}

		// store.setLoaderOverlayOpen(true)
		const formData = parseForSubmitRequest(userRequestFormData)

		// Find out the `projectId`
		let { value: selectedPickListValue } =
			formData.find(({ fieldId }) => fieldId == picklistFieldId) || {}

		const projectId = getProjectIdFromConfiguredCollections(
			formStore.userSelectedForm.collections,
			selectedPickListValue
		)
		store.setLoaderOverlayOpen(true)
		const { ok, data, statusCode } = await AppRequests("Requests").post<
			any,
			IPOST_RESPONSE_Requests
		>(instanceAnonymousUsers ? "anonym" : null, formData, {
			requestedItemId: formStore.userSelectedForm.id,
		})

		if (ok && data) {
			let { itemId, documentKey } = data
			itemId && (await sendAttachments(files, projectId, itemId, documentKey))

			// Obj for all replacments
			const replaceTextObj = {
				"{{documentKey}}": documentKey,
			}
			store.setInfoModalConfig({
				type: failedFileNames.length ? "warning" : "info",
				titleKey: failedFileNames.length
					? "newRequest.success.header-warning"
					: "newRequest.success.header",
				descriptionKey: isEnabled
					? failedFileNames.length
						? `${replaceMultipleInString(
								isEN ? notificationTextEN : notificationTextLV,
								replaceTextObj
						  )} ${t("newRequest.success.description-warn")} ${failedFileNames.join(", ")}`
						: replaceMultipleInString(
								isEN ? notificationTextEN : notificationTextLV,
								replaceTextObj
						  )
					: failedFileNames.length
					? `${t("newRequest.success.description-warn")} ${failedFileNames.join(", ")}`
					: `${t("newRequest.success.description")}`,
			})
			// Clear itemOfTypeOptionsForm
			clearFormValues()
			// Clear Form data in UserSideNewRequestForm
			setTriggerClearValues(true)
		} else if (statusCode == 401) {
			setUser(undefined)
			lostOrFinishedUserSession(store)
		} else if (statusCode == 423) {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "newRequest.failed.locked",
				descriptionKey: "newRequest.failed.locked-description",
			})
		} else {
			// ! TECHNICAL DEBT :  "Vajag nodefinēt visus iemeslus kāpēc pieteikums varētu failot"
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "newRequest.failed.header",
				descriptionKey: "newRequest.failed.description",
			})
		}

		store.triggerSessionTimeout()
		store.setLoaderOverlayOpen(false)
	}

	const clearFormValues = () => {
		for (let fieldKey in itemOfTypeOptionsForm) {
			delete itemOfTypeOptionsForm[fieldKey]
		}
	}

	useEffect(() => {
		setIsLoading(true)
		getCollection()
	}, [store.selectedUserUISection, formStore.userSelectedForm])

	/**
	 * First API call for building a form,
	 * Used later to get Item of Type pick-list options
	 */
	const getCollection = async () => {
		if (!formStore.userSelectedForm) {
			setIsLoading(false)
			return
		}

		const { ok, data } = await AppRequests("Collection").get<ICollection[]>(undefined, {
			requestedItemId: formStore.userSelectedForm.id,
		})
		if (ok && data) {
			setPicklistFieldId(data[0]?.picklistFieldId ?? undefined)
			setCollection(data)
			if (!data[0]?.picklistFieldId) {
				getNewRequestFormConfig(formStore.userSelectedForm.id, data[0].projectId ?? undefined)
			} else {
				getNewRequestFormConfig(formStore.userSelectedForm.id)
			}
		} else {
			setIsLoading(false)
		}
		store.triggerSessionTimeout()
	}

	/**
	 * Gets initial request form fields
	 */
	const getNewRequestFormConfig = async (id: string, projectId?: string) => {
		const { ok, data } = await AppRequests("RequestForms").get<INewRequestFormField[]>(
			"newRequestForm",
			{ requestedItemId: id }
		)

		if (ok && data) {
			if (formStore.userSelectedForm) {
				formStore.setUserFormFields(data)
			} else {
				formStore.setUserFormFields([])
			}
			if (projectId) {
				expandInitialFormProperties(projectId)
			} else {
				expandInitialFormProperties()
			}
		} else {
			formStore.setUserFormFields([])
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "system.failed",
			})
		}

		setIsLoading(false)
	}

	/**
	 * Here you can specify how each field in form should have additional functionality
	 */
	const expandInitialFormProperties = (selectedProjectId?: string) => {
		formStore.userFormFields.map(async (field, index) => {
			/**
			 * Item of type has a linked field
			 * This field has a property "projectId"
			 * From that property item of type values are filtered
			 */
			if (field.fieldType === "ITEM_OF_TYPE") {
				let _userField = field?.itemOfTypeUserFieldId
				let _userItem = field?.itemOfTypeUserItemId
				// if itemTypeBoolean is selected then endpoint which returns already filtered itemOfTypeOptions (optional userField)
				// if itemTypeUser is selected then endpoint which returns already filtered itemOfTypeOptions
				// if itemTypeBoolean isn't selected then endpoint which returns item fields
				if (field.itemOfTypeBooleanFieldId && field.itemOfTypeBooleanItemId) {
					const avaibleOptions = await getItemOptions(
						field.itemOfTypeBooleanFieldId,
						field.itemOfTypeBooleanItemId,
						field.mainFieldLabel,
						_userField,
						true
					)
					if (avaibleOptions) {
						const pickListValues = await filterItemOfTypeOptions(avaibleOptions, selectedProjectId)
						mergeUserFormFields(index, { pickListValues })
					}
				} else if (_userField && _userItem) {
					const avaibleOptions = await getItemOptions(
						_userField,
						_userItem,
						field.mainFieldLabel,
						undefined,
						false
					)
					if (avaibleOptions) {
						const pickListValues = await filterItemOfTypeOptions(avaibleOptions, selectedProjectId)
						mergeUserFormFields(index, { pickListValues })
					}
				} else {
					const itemFields = await getItemFields(field)
					if (itemFields) {
						const pickListValues = await getItemOfTypeOptions(
							itemFields,
							field.mainFieldKey,
							selectedProjectId
						)
						mergeUserFormFields(index, { pickListValues })
					}
				}
			}
		})
	}

	/**
	 * Based on "projectId" either itemOfType available options are filtered
	 * If linked form field is not selected, all options are available
	 */
	const getItemOfTypeOptions = async (
		itemFields: TItemTypeFields[],
		fieldName: string,
		selectedProjectId?: string
	) => {
		const options: TReactSelectOption[] = new Array()

		itemFields.map(({ fieldId, itemOfTypeValues }) => {
			if (fieldId == fieldName) {
				itemOfTypeValues?.map(({ label, value, projectId }) => {
					return selectedProjectId
						? selectedProjectId == projectId && options.push({ value: value, label: label })
						: options.push({ value: value, label: label })
				})
			}
		})
		return options
	}
	/**
	 * Based on "projectId" either itemOfType available options are filtered
	 */
	const filterItemOfTypeOptions = async (
		selectOptions: TItemOfTypeValues[],
		selectedProjectId?: string
	) => {
		const options: TReactSelectOption[] = new Array()

		selectOptions?.map(({ label, value, projectId, userValue }) => {
			return selectedProjectId
				? selectedProjectId == projectId &&
						options.push({ value: value, label: label, userProp: userValue })
				: options.push({ value: value, label: label, userProp: userValue })
		})

		return options
	}

	/**
	 * Gets all item fields for chosen item
	 * Chosen item is the one, that controls itemOfType pick list options
	 */
	const getItemFields = async (field: INewRequestFormField) => {
		const itemsAreCollected: boolean = itemFields.length > 0
		if (itemsAreCollected) {
			return itemFields
		} else {
			const itemId: string = readIdFromName(field.mainFieldKey)
			const { ok, data } = await AppRequests("ItemType").get<TItemTypeFields[]>("fields", {
				requestedItemId: itemId,
			})

			if (ok && data) {
				setItemFields(data)
				return data
			}
		}
	}
	/**
	 * Gets all item options if itemOfTypeBoolean is selected
	 */
	const getItemOptions = async (
		itemOfTypeBooleanFieldId: string,
		itemOfTypeBooleanItemId: string,
		fieldKey: string,
		itemOfTypeUser?: string,
		isBool?: boolean
	) => {
		const has = Object.prototype.hasOwnProperty // cache the lookup once, in module scope.
		const hasKey: boolean = has.call(itemOfTypeOptionsForm, fieldKey)

		const itemOfTypeOptionsAreCollected: boolean =
			hasKey && itemOfTypeOptionsForm[fieldKey] ? itemOfTypeOptionsForm[fieldKey].length > 0 : false

		if (itemOfTypeOptionsAreCollected) {
			return itemOfTypeOptionsForm[fieldKey]
		} else {
			const { ok, data } = isBool
				? await AppRequests("ItemType").get<TItemOfTypeValues[]>("ItemOfTypeValuesFiltered", {
						itemOfTypeId: itemOfTypeBooleanItemId,
						filterFieldId: itemOfTypeBooleanFieldId,
						query: {
							userFieldId: itemOfTypeUser ?? "",
						},
				  })
				: await AppRequests("ItemType").get<TItemOfTypeValues[]>("ItemOfTypeValuesFilter", {
						itemOfTypeId: itemOfTypeBooleanItemId,
						filterFieldId: itemOfTypeBooleanFieldId,
				  })

			if (ok && data) {
				itemOfTypeOptionsForm[fieldKey] = data
				return data
			}
		}
	}

	/**
	 * Each time values are changed, itemOfType pick list options are updated
	 */
	const onDataChange = (values: IFormVal) => {
		if (!picklistFieldId || !values[picklistFieldId]) return null
		// ! TECHNICAL DEBT : If option is not available, clear value
		collection.map(({ picklistValue, projectId }) => {
			if (picklistValue == values[picklistFieldId]) {
				expandInitialFormProperties(projectId)
			}
		})
	}

	if (store.isLoaderOverlayOpen) {
		return <></>
	}

	if (isLoading) {
		return (
			<HyperDiv centered width="100%" height="100%">
				<LoadingText />
			</HyperDiv>
		)
	}

	return (
		<HyperDiv
			direction="column"
			height="100%"
			padding="10px 0px"
			width="100%"
			style={{ overflowY: "auto" }}
			centered={isLoading}
		>
			{/* Welcome text (admin can modify) */}
			<UserFormWelcomeText />

			{/* Loader */}
			{/* Form is configured and loaded successfully */}
			{formStore.userFormFields.length > 0 && (
				<HyperDiv centered>
					<HyperDiv padding="50px 0px 0px 0px" width={isMobile ? "80%" : "60%"}>
						<h1>{t("user-request.make-new")}</h1>
						<SepLine margin={isMobile ? ".5rem 0" : "2rem 0"} />
						<UserSideNewRequestForm
							config={formStore.userFormFields}
							onChangeForm={onDataChange}
							onSubmitForm={onUserSubmitsNewRequest}
							resetTriggerClearValues={() => setTriggerClearValues(false)}
							triggerClearValues={triggerClearValues}
						/>
					</HyperDiv>
				</HyperDiv>
			)}
			{/* Admin has not configured form, returns empty array */}
			{!isLoading && formStore.userFormFields.length === 0 && (
				<HyperDiv style={{ marginTop: 50, textAlign: "center" }}>
					<FormFieldLabel>{t("user-request.no-config")}</FormFieldLabel>
				</HyperDiv>
			)}
			{/* Request fails */}
			{!isLoading && !formStore.userFormFields && (
				<HyperDiv style={{ marginTop: 50, textAlign: "center" }}>
					<FormFieldLabel>{t("system.failed")}</FormFieldLabel>
				</HyperDiv>
			)}
			<Whitespace x="2" />
		</HyperDiv>
	)
})

export default UserSubmitRequest
